import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowRight, GraduationCap, Search, Share2, Sparkles, School } from 'lucide-react';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from 'react-share';
import neighborhoodMap from '../../data/neighborhoodMap';
import { routingKeyToCounty } from '../../data/routingKeyToCounty';

type PlaceType = 'neighborhood' | 'district' | 'county';

interface DiscoverPlace {
  slug: string;
  name: string;
  county: string;
  type: PlaceType;
  rentPath: string;
}

const toTitleCase = (value: string) =>
  value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const SchoolsAroundDiscoverPage: React.FC = () => {
  const [query, setQuery] = useState('');
  const [countyFilter, setCountyFilter] = useState('all');
  const [placeTypeFilter, setPlaceTypeFilter] = useState<'all' | PlaceType>('all');

  const areas = useMemo(() => neighborhoodMap.getAllAreas(), []);

  const places = useMemo<DiscoverPlace[]>(() => {
    const neighborhoodPlaces: DiscoverPlace[] = areas.map((item) => ({
      slug: item.areaSlug,
      name: item.areaName,
      county: item.county,
      type: 'neighborhood',
      rentPath: `/browse?location=${encodeURIComponent(item.areaName)}`,
    }));

    const dublinDistrictSlugs = Array.from(
      new Set(
        Object.keys(routingKeyToCounty)
          .filter((key) => /^D\d{2}[A-Z]?$/i.test(key))
          .map((key) => {
            const districtPart = key.slice(1).toLowerCase();
            const numericPart = districtPart.match(/^(\d+)([a-z]?)$/);
            if (numericPart) {
              return `dublin-${parseInt(numericPart[1], 10)}${numericPart[2]}`;
            }
            return `dublin-${districtPart}`;
          })
      )
    ).sort((a, b) => {
      const matchA = a.match(/^dublin-(\d+)([a-z]?)$/i);
      const matchB = b.match(/^dublin-(\d+)([a-z]?)$/i);
      if (!matchA || !matchB) return a.localeCompare(b);
      const numDiff = Number(matchA[1]) - Number(matchB[1]);
      if (numDiff !== 0) return numDiff;
      return matchA[2].localeCompare(matchB[2]);
    });

    const districtPlaces: DiscoverPlace[] = dublinDistrictSlugs.map((slug) => {
      const districtLabel = slug.replace('dublin-', '').toUpperCase();
      return {
        slug,
        name: `Dublin ${districtLabel}`,
        county: 'Dublin',
        type: 'district',
        rentPath: `/browse?location=Dublin%20${districtLabel}`,
      };
    });

    const countySlugs = Array.from(
      new Set(Object.values(routingKeyToCounty).map((county) => county.toLowerCase()))
    ).sort((a, b) => a.localeCompare(b));
    const countyPlaces: DiscoverPlace[] = countySlugs.map((countySlug) => ({
      slug: `county-${countySlug}`,
      name: toTitleCase(countySlug),
      county: toTitleCase(countySlug),
      type: 'county',
      rentPath: `/browse?location=${encodeURIComponent(toTitleCase(countySlug))}`,
    }));

    const seen = new Set<string>();
    return [...districtPlaces, ...countyPlaces, ...neighborhoodPlaces].filter((item) => {
      if (seen.has(item.slug)) return false;
      seen.add(item.slug);
      return true;
    });
  }, [areas]);

  const counties = useMemo(
    () => [
      'all',
      ...Array.from(new Set(places.map((item) => item.county))).sort((a, b) => a.localeCompare(b)),
    ],
    [places]
  );

  const filtered = useMemo(() => {
    const cleanQuery = query.trim().toLowerCase();
    return places
      .filter((item) => (countyFilter === 'all' ? true : item.county === countyFilter))
      .filter((item) => (placeTypeFilter === 'all' ? true : item.type === placeTypeFilter))
      .filter((item) =>
        cleanQuery
          ? item.name.toLowerCase().includes(cleanQuery) ||
            item.slug.toLowerCase().includes(cleanQuery) ||
            item.county.toLowerCase().includes(cleanQuery)
          : true
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [places, countyFilter, placeTypeFilter, query]);

  const canonicalUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/schools-around`
      : 'https://rentsmart.ie/schools-around';
  const shareUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;
  const shareText =
    'Schools around every Irish neighborhood with catchment signals, moving tips, and direct home search CTAs on RentSmart.ie.';

  return (
    <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-slate-50">
      <Helmet>
        <title>Schools Around Every Irish Neighbourhood | RentSmart.ie</title>
        <meta
          name="description"
          content="Find schools around Irish neighbourhoods with practical catchment signals, admissions planning tips, and direct rental or sale home search links."
        />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <header className="bg-slate-900 text-white pt-14 pb-16 px-4 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="absolute -bottom-16 -left-6 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-widest mb-5">
            <GraduationCap className="h-4 w-4" /> Family Move Planning
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
            Schools Around Every Neighbourhood
          </h1>
          <p className="text-slate-300 max-w-3xl text-lg leading-relaxed">
            Start with school-access signals, then jump straight into homes for rent or sale in the
            same area. Built to speed up family relocation decisions.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest text-slate-400">
              <Share2 className="h-3.5 w-3.5" /> Share
            </span>
            <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
              <FacebookIcon size={30} round />
            </FacebookShareButton>
            <TwitterShareButton url={shareUrl} title={shareText}>
              <TwitterIcon size={30} round />
            </TwitterShareButton>
            <WhatsappShareButton url={shareUrl} title={shareText}>
              <WhatsappIcon size={30} round />
            </WhatsappShareButton>
            <LinkedinShareButton
              url={shareUrl}
              title="Schools Around Neighbourhoods | RentSmart.ie"
              summary={shareText}
              source="RentSmart.ie"
            >
              <LinkedinIcon size={30} round />
            </LinkedinShareButton>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 -mt-8 pb-16">
        <section className="mt-12 rounded-[2rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
          <div className="grid md:grid-cols-12 gap-4">
            <div className="md:col-span-6">
              <label className="text-xs font-black uppercase tracking-wider text-slate-500">
                Search place
              </label>
              <div className="mt-2 relative">
                <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search by area or county..."
                  className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-4 py-3 text-sm font-semibold text-slate-800"
                />
              </div>
            </div>
            <div className="md:col-span-4">
              <label className="text-xs font-black uppercase tracking-wider text-slate-500">
                County
              </label>
              <select
                value={countyFilter}
                onChange={(event) => setCountyFilter(event.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800"
              >
                {counties.map((county) => (
                  <option key={county} value={county}>
                    {county === 'all' ? 'All counties' : county}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-black uppercase tracking-wider text-slate-500">
                Place type
              </label>
              <select
                value={placeTypeFilter}
                onChange={(event) => setPlaceTypeFilter(event.target.value as 'all' | PlaceType)}
                className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800"
              >
                <option value="all">All types</option>
                <option value="county">County</option>
                <option value="district">Dublin district</option>
                <option value="neighborhood">Neighborhood</option>
              </select>
            </div>
            <div className="md:col-span-12 lg:col-span-2 flex items-end">
              <div className="w-full rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-center">
                <p className="text-[10px] uppercase tracking-wider font-black text-emerald-700">
                  Results
                </p>
                <p className="text-xl font-black text-emerald-900">{filtered.length}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.slice(0, 180).map((item) => (
            <div
              key={item.slug}
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[10px] font-black uppercase tracking-wider text-slate-600 mb-3">
                <School className="h-3.5 w-3.5" /> {item.county}
              </div>
              <h2 className="text-xl font-black text-slate-900 mb-2">{item.name}</h2>
              <p className="text-sm text-slate-600 mb-5 leading-relaxed">
                Explore school-access planning insights and then move directly to homes in this
                area.
              </p>
              <div className="flex flex-wrap gap-2">
                <Link
                  to={`/schools-around/${item.slug}`}
                  className="no-underline inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-black uppercase tracking-wider text-white"
                >
                  View schools <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link
                  to={item.rentPath}
                  className="no-underline inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-black uppercase tracking-wider text-emerald-800"
                >
                  Search rentals
                </Link>
              </div>
            </div>
          ))}
        </section>

        {filtered.length > 180 ? (
          <section className="mt-6 rounded-2xl border border-slate-200 bg-slate-100 p-5">
            <p className="text-sm font-semibold text-slate-700">
              Showing first 180 areas. Narrow your search to view specific neighborhoods faster.
            </p>
          </section>
        ) : null}

        <section className="mt-8 rounded-2xl border border-cyan-200 bg-cyan-50 p-5">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-cyan-700 mt-0.5" />
            <p className="text-sm text-cyan-900/90 font-semibold leading-relaxed">
              These pages provide directional school-access planning signals, not official
              admissions or enrollment guarantees. Always verify details with schools and local
              authorities.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SchoolsAroundDiscoverPage;
